import React from "react";

import { useTranslation } from "utils";

import { CROP_TYPE_FCPO } from "models";

import {
  Card,
  Checkbox,
  CropRotationCardProps,
  CropRotationRecommendationCard,
  Flex,
  HideFCPOFeatureForUnenrolledFarms,
  Icon,
  PracticeBundleOpts,
  TooltipWrapper,
  YearlyEmptyCropCard,
} from "components";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";

export type YearlyCropCardProps = Partial<CropRotationCardProps> & {
  nameContainerRef: React.RefObject<HTMLParagraphElement>;
  nameRef: React.RefObject<HTMLParagraphElement>;
  shouldShowNameTooltip: boolean;
  shouldHideIcons: boolean;
  shouldShowEuroIcon: boolean;
  shouldHideBaselineTag: boolean;
  isNarrow: boolean;
  isSelected: boolean;
  activePracticeBundles: PracticeBundleOpts[];
  isEmptyWithNoEnrollment?: boolean;
  isBaseline?: boolean;
  isConfirmed?: boolean;
  isFetching: boolean;
  isReportingYear?: boolean;
  cropMixId?: string;
  isFlaggedByVerifier?: boolean;
  // Multi-selection props
  isChecked?: boolean;
  isSelectable?: boolean;
  openTasksCount?: number;
  fieldId: string;
  year: number;
};

export const CompletenessDataBadge = ({
  isComplete,
  isConfirmed,
  openTaskCount,
  className,
}: {
  isComplete: boolean;
  isConfirmed: boolean;
  openTaskCount?: number;
  className?: string;
}) => (
  <Flex className={className} align="center" justify="center" data-testid={`completeness-data-badge-${isComplete ? "complete" : "incomplete"}`}>
    {isConfirmed && !openTaskCount ? (
      <Icon iconName={"tabler/lock"} className={"icon inline lock-icon"} size="s" />
    ) : isComplete ? (
      <Icon iconName={"tabler/check"} className={"icon inline checkmark-icon"} size="s" />
    ) : (
      <span className="open-task-badge-small">{openTaskCount ?? 0}</span>
    )}
  </Flex>
);

// card from HF (yearly view card)
export const YearlyCropCard = ({
  cropName,
  cropId,
  fieldId,
  payout,
  currency,
  dataTestId,
  isSelected,
  isNarrow,
  nameContainerRef,
  nameRef,
  shouldShowNameTooltip,
  shouldHideIcons,
  cropType,
  activePracticeBundles = [],
  isEmptyCard,
  isEmptyWithNoEnrollment = false,
  isMandatory,
  isFetching,
  onClick,
  recommendations,
  enrolledInInventoryProgram,
  isReportingYear,
  isConfirmed,
  cropMixId,
  isChecked = false,
  isInactive = false,
  openTasksCount,
  year,
  isFlaggedByVerifier = false,
}: YearlyCropCardProps) => {
  const { t } = useTranslation("fieldTimeline");
  const { isSubmissionFeatureEnabled } = useSubmissionStatus();

  const getIconBySlug = (slug: string) =>
    ({
      fertilizer: "klim/fertiliser",
      tillage: "klim/tillage",
    })[slug] ?? "";

  if (isEmptyCard) {
    return (
      <YearlyEmptyCropCard
        fieldId={fieldId}
        handleClick={isFetching ? () => {} : onClick}
        payout={payout}
        isSelected={isSelected}
        cropType={cropType!}
        isNarrow={isNarrow}
        dataTestId={dataTestId}
        isMandatory={isMandatory ?? false}
        isEmptyWithNoEnrollment={isEmptyWithNoEnrollment}
        isFetching={isFetching}
        isChecked={isChecked}
        isInactive={isInactive}
        year={year}
        isFlaggedByVerifier={isFlaggedByVerifier}
        openTasksCount={openTasksCount}
      />
    );
  }

  if (recommendations) {
    return (
      <CropRotationRecommendationCard
        cropType={cropType!}
        payout={payout}
        currency={currency}
        year={year}
        recommendations={recommendations}
        isSelected={isSelected}
        dataTestId={dataTestId}
        className="yearly-view"
        isInactive={isInactive}
      />
    );
  }

  const NewSubmission = () =>
    !shouldHideIcons && (
      <CompletenessDataBadge isComplete={openTasksCount === 0 || !openTasksCount} openTaskCount={openTasksCount} isConfirmed={!!isConfirmed} />
    );

  const OldSubmission = () => (
    <HideFCPOFeatureForUnenrolledFarms>
      <>
        {!shouldHideIcons && (
          <>
            {cropType !== CROP_TYPE_FCPO.UNDERSOWING
              ? activePracticeBundles?.map((bundle) => (
                  <TooltipWrapper
                    key={bundle.slug}
                    tooltip={{ placement: "top", title: t(`cropCard.practiceBundles.${bundle.slug}`) }}
                    className={`practice-bundle-icon ${bundle.hasMissingData ? "incomplete" : ""}`}
                  >
                    <Icon iconName={getIconBySlug(bundle.slug)} className="icon inline" dataTestId={`icon-${bundle.slug}`} />
                  </TooltipWrapper>
                ))
              : null}
          </>
        )}
        {!shouldHideIcons && enrolledInInventoryProgram && isReportingYear && (
          <CompletenessDataBadge isComplete={openTasksCount === 0 || !openTasksCount} openTaskCount={openTasksCount} isConfirmed={!!isConfirmed} />
        )}
      </>
    </HideFCPOFeatureForUnenrolledFarms>
  );

  return (
    <Card
      className={`crop-rotation-card layout  ${isSelected ? "selected" : isInactive ? "inactive-card" : ""} ${isNarrow ? "is-narrow" : ""} ${
        !cropId ? "unknown" : ""
      }`}
      data-fieldid={fieldId}
      data-testid={`crop-rotation-card-${dataTestId}`}
      tabIndex={0}
      onClick={isFetching ? () => {} : onClick}
    >
      <Flex align="center" justify="center" gap="0.5rem">
        {/* Handling onClick in AllFieldsTimelines (handleCardClick()) to also check the box when the crop card (not just the checkbox) is clicked. */}
        {!isInactive && !isConfirmed && <Checkbox checked={isChecked} data-testid={`crop-checkbox-${dataTestId}`} />}
        <Flex
          ref={nameContainerRef}
          data-testid={`crop-rotation-card-${dataTestId}-header-name`}
          className={`name-container ${isNarrow ? "is-narrow" : ""}`}
          align="center"
          gap=".125rem"
        >
          <TooltipWrapper
            tooltip={
              shouldShowNameTooltip || isNarrow || shouldHideIcons
                ? {
                    placement: "topLeft",
                    title: <>{(shouldShowNameTooltip || isNarrow) && cropName}</>,
                  }
                : {}
            }
          >
            <Flex align="center" gap="0.25rem">
              {cropMixId && <Icon className="cmc-plant-icon" iconName="klim/plant-ai" />}
              <span ref={nameRef} className="name">
                {isNarrow ? "..." : cropName}
              </span>
            </Flex>
          </TooltipWrapper>
        </Flex>
        {isSubmissionFeatureEnabled ? <NewSubmission /> : <OldSubmission />}
      </Flex>
    </Card>
  );
};
